import Feather from '@expo/vector-icons/Feather';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  CustomFieldLocation,
  CustomFieldsModel,
  CustomFieldValuesModel,
  MeetingLinksType,
  MeetingLocationsModel,
  MeetingsModel,
  MeetingTypesModel,
  PrimaryKeyType,
  WhatsappPlan,
} from '@w3lcome/types';
import { Spin, Header, EmailTagInput, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import Input from '_/components/Input';
import WebDateTimePicker from '_/components/WebDateTimePicker';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { validEmail } from '_/helpers/functions';
import { getDateLocale } from '_/helpers/getDateLocale';
import { AppRoute, EditMeetingProps } from '_/routes/types';
import {
  customFieldsApi,
  hostsApi,
  meetingLinksApi,
  meetingLocationsApi,
  meetingsApi,
  meetingTypeFlowAllowsApi,
  meetingTypesApi,
} from '_/services/api';
import { addHours, format } from 'date-fns';
import * as Localization from 'expo-localization';
import moment from 'moment-timezone';
import { transparentize, lighten } from 'polished';
import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  Keyboard,
  Platform,
  TextInput,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Image,
  StyleSheet,
  TouchableWithoutFeedback,
  FlatList,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateMeetingSuccess,
  meetingFailure,
  createLinkRequest,
  createLinkFailure,
  createMeetingSuccess,
} from '_/store/modules/meeting/actions';
import CustomFieldsInput from './CustomFields';
import ModalSelector from './ModalOptionsSelector';
import ModalNumberSelector from './ModalNumberSelector';
import { IdNumberGuest, MeetingGuest, useMeetingsContext } from '_/hooks/MeetingsProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import WhatsappTagInput from '_/components/WhatsappTagInput';
import CardholderSearchApp from './Cardholdersearch';
import { GUEST_LINKS_QUERY_KEY } from '../Meeting/GuestList';
import { isValidPhoneNumber } from 'libphonenumber-js';
interface CustomFieldWithError extends CustomFieldsModel {
  hasError?: boolean;
}

interface MeetingCustomFields extends MeetingsModel {
  customFields?: CustomFieldsModel[];
  guests?: MeetingGuest[];
}

const PAGE_SIZE = 50;

export default function EditMeeting() {
  const navigation = useNavigation<EditMeetingProps>();
  const dispatch = useDispatch();

  const userId = useSelector((state: any) => state.user.id);

  const { params } = useRoute();
  const { isCredentialing = false, onChange }: any = params ?? {};

  const host = useSelector((state: any) => state.host);
  const companyId = useSelector((state: any) => state.company.id);
  const { defaultMeetingLocation, defaultMeetingTypeId, name } = useSelector(
    (state: any) => state.host
  );
  const { loading } = useSelector((state: any) => state.loading);
  const meeting = useSelector((state: any) => state.meeting?.meeting);
  const { multiDayMeetingEnabled, whatsappPlan } = useSelector(
    (state: any) => state.company.customization
  );

  const isEditMode = useRef(!!meeting?.id);
  const { i18n, t: translate } = useTranslation();
  const { visitFlowTypes } = useMeetingsContext();
  const locale = getDateLocale(i18n.language);

  const [idNumberGuest, setIdNumberGuest] = useState<IdNumberGuest | null>(null);
  const [guestsToCheckinByHost, setGuestsToCheckinByHost] = useState<any[]>([]);

  const fetchingRef = useRef(false);

  const [values, setValues] = useState<Partial<MeetingCustomFields>>({
    title: meeting?.title || '',
    message: meeting?.message || '',
    location: meeting?.location || '',
    notes: meeting?.notes || '',
    start: meeting?.start || new Date(),
    end: meeting?.end || addHours(new Date(), 1),
    createdOn: meeting?.createdOn || 'MOBILE',
    timezone: meeting?.timezone || Localization.getCalendars()[0]?.timeZone || 'America/Sao_Paulo',
    customFields: meeting?.customFieldValues || [],
    meetingLocationId: undefined,
    coHosts: meeting?.coHosts || [],
    guests: [],
    visitFlowTypeId: meeting?.visitFlowTypeId || visitFlowTypes[0]?.id,
  });

  const [limitOfLinkInvites, setLimitOfLinkInvites] = useState('');
  const [selectMeetingType, setSelectMeetingType] = useState<MeetingTypesModel | null>(null);
  const [locations, setLocations] = useState<MeetingLocationsModel[]>([]);
  const [hasInputFocus, setHasInputFocus] = useState(false);
  const [isDatePickerVisible, setDatePickerVisibility] = useState(false);
  const [currentDatePicker, setCurrentDatePicker] = useState('');
  const [guestEmailInput, setguestEmailInput] = useState('');
  const [hostEmailInput, sethostEmailInput] = useState('');
  const [location, setLocation] = useState('');
  const [disabledLocation, setDisabledLocation] = useState('');

  const [activeTab, setActiveTab] = useState<'email' | 'whatsapp'>('email');

  const titleRef = useRef<TextInput>(null);
  const messageRef = useRef<TextInput>(null);
  const locationRef = useRef<TextInput>(null);
  const notesRef = useRef<TextInput>(null);

  const [loadingInvitees, setLoadingInvitees] = useState(false);

  const queryClient = useQueryClient();

  const isWhatsappEnabled = whatsappPlan === WhatsappPlan.PRE_REGISTRATION_VISITOR_WHATSAPP;

  const meetingId = meeting?.id;

  const [filteredFlowTypes, setFilteredFlowTypes] = useState(visitFlowTypes);

  const idNumberGuestFields = useMemo(
    () => (selectMeetingType?.customFields ?? []).filter((f) => f.location === 'GUEST_EXTRA_INFO'),
    [selectMeetingType?.id]
  );

  const primaryKey = (visitFlowTypes.find((vft) => vft.id === values.visitFlowTypeId)?.primaryKey ??
    PrimaryKeyType.CPF) as PrimaryKeyType;

  useEffect(() => {
    if (!selectMeetingType || !visitFlowTypes.length) return;
    filterFlowsByMeetingType(selectMeetingType);
  }, [selectMeetingType, visitFlowTypes]);

  async function filterFlowsByMeetingType(meetingType: MeetingTypesModel) {
    if (!meetingType?.restrictVisitFlows) {
      setFilteredFlowTypes(visitFlowTypes);
      setValues((prev) => {
        if (!prev.visitFlowTypeId && visitFlowTypes.length > 0) {
          return { ...prev, visitFlowTypeId: visitFlowTypes[0].id };
        }
        return prev;
      });
      return;
    }
    try {
      const allowedIds = new Set(await meetingTypeFlowAllowsApi.getByMeetingType(meetingType.id));
      const allowed = visitFlowTypes.filter((flow) => allowedIds.has(flow.id));
      setFilteredFlowTypes(allowed);
      setValues((prev) => {
        const currentIsValid = prev.visitFlowTypeId && allowedIds.has(prev.visitFlowTypeId);
        if (!currentIsValid && allowed.length > 0) {
          return { ...prev, visitFlowTypeId: allowed[0].id };
        }
        return prev;
      });
    } catch {
      setFilteredFlowTypes(visitFlowTypes);
    }
  }

  const { data: meetingTypes = [], isLoading: meetingTypesLoading } = useQuery({
    queryKey: ['getMeetingTypes', isCredentialing, companyId, userId],
    queryFn: async () => {
      try {
        return await getMeetingTypes();
      } catch (error: any) {
        showMessage({ message: translate('personalMeeting.internalError'), type: 'danger' });
        throw error;
      }
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data: allLocations = [], isLoading: allLocationsLoading } = useQuery({
    queryKey: ['getMeetingLocations', companyId, userId],
    queryFn: async () => {
      try {
        return await getMeetingLocations();
      } catch (error: any) {
        showMessage({ message: translate('personalMeeting.internalError'), type: 'danger' });
        throw error;
      }
    },
    staleTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    if (meetingTypes || defaultMeetingTypeId) {
      const meetingTypeId = meeting?.meetingTypeId || defaultMeetingTypeId;
      const defaultMeetingType = meetingTypes?.find(
        (type: MeetingTypesModel) => type.id === meetingTypeId
      );
      setSelectMeetingType(defaultMeetingType || meeting?.meetingType || meetingTypes?.[0]);
    }
  }, [meetingTypes, defaultMeetingTypeId]);

  useEffect(() => {
    setDefaultMeetingLocation();
  }, [meeting, defaultMeetingLocation]);

  useEffect(() => {
    if (!isEditMode.current) return;
    if (!meetingId || !selectMeetingType) return;
    fetchInvitees();
  }, [meetingId, selectMeetingType?.id]);

  async function fetchInvitees() {
    if (!meetingId) return;
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setLoadingInvitees(true);

    try {
      if (selectMeetingType?.idNumberSearchEnabled) {
        const { data } = await meetingLinksApi.getLinksByMeeting(
          meetingId,
          MeetingLinksType.GUEST,
          {
            $limit: 1,
            $skip: 0,
          }
        );

        if (data.length > 0) {
          const link = data[0];
          setIdNumberGuest({
            idNumber: link.idNumber || '',
            email: link.email || undefined,
            phone: link.phone || undefined,
            name: link.name || undefined,
            linkId: link.id,
          });
        }
        return;
      }

      let skip = 0;
      const LIMIT = 100;
      const allGuests: MeetingGuest[] = [];

      while (true) {
        const { data, total } = await meetingLinksApi.getLinksByMeeting(
          meetingId,
          MeetingLinksType.GUEST,
          { $limit: LIMIT, $skip: skip }
        );

        if (!data?.length) break;

        data.forEach((l: any) => {
          allGuests.push({
            name: l.name || undefined,
            email: l.email || undefined,
            phone: l.phone || undefined,
            idNumber: l.idNumber || undefined,
          });
        });

        if (skip + data.length >= total) break;
        skip += LIMIT;
      }

      setValues((prev) => ({ ...prev, guests: allGuests }));

      const hasWhatsappOnly = allGuests.every((g) => g.phone && !g.email);
      setActiveTab(hasWhatsappOnly ? 'whatsapp' : 'email');
    } catch {
      showMessage({ message: translate('personalMeeting.internalError'), type: 'danger' });
    } finally {
      fetchingRef.current = false;
      setLoadingInvitees(false);
    }
  }

  async function updateMeeting(meetingObj: any) {
    try {
      const updatedMeeting = await meetingsApi.patchCurrentMeeting(meetingObj);
      dispatch(updateMeetingSuccess(updatedMeeting));
      onChange?.(updatedMeeting);
      queryClient.invalidateQueries({ queryKey: GUEST_LINKS_QUERY_KEY(meetingObj.id) });
      showMessage({ message: translate('meetingSaga.updateMeetingSuccess'), type: 'success' });
      navigation.navigate(AppRoute.MEETING, { isCredentialing });
    } catch {
      dispatch(meetingFailure());
      showMessage({ message: translate('meetingSaga.updateMeetingError'), type: 'danger' });
    }
  }

  async function createMeeting(meetingObj: any) {
    try {
      const newMeeting = await meetingsApi.createNewMeeting(meetingObj);
      if (meetingObj.limit) {
        try {
          dispatch(createLinkRequest(meetingObj.limit, newMeeting.id, newMeeting.companyId));
        } catch {
          dispatch(createLinkFailure());
        }
      }
      if ((meetingObj.guests ?? []).length > 0) {
        queryClient.setQueryData(
          GUEST_LINKS_QUERY_KEY(newMeeting.id),
          (meetingObj.guests as MeetingGuest[]).map((g) => ({ ...g, linkUrl: undefined }))
        );
      }

      meetingObj?.guestsToCheckinByHost?.length > 0 && selectMeetingType?.idNumberSearchEnabled
        ? showMessage({
            message: translate('registrationSuccessWaitApproval'),
            type: 'success',
            duration: 5000,
          })
        : showMessage({ message: translate('meetingSaga.createMeetingSuccess'), type: 'success' });
      onChange?.(newMeeting);
      dispatch(createMeetingSuccess(newMeeting));
      navigation.navigate(AppRoute.MEETING, { isCredentialing });
    } catch {
      dispatch(meetingFailure());
      showMessage({ message: translate('meetingSaga.createMeetingError'), type: 'danger' });
    }
  }

  const customFields = useMemo(() => {
    if (meeting?.meetingType?.customFields) {
      return (
        meeting.meetingType.customFields.filter(
          (f: CustomFieldsModel) => f.location === 'MEETING_HOST'
        ) || []
      );
    }
    return (
      selectMeetingType?.customFields?.filter(
        (f: CustomFieldsModel) => f.location === 'MEETING_HOST'
      ) || []
    );
  }, [meeting, selectMeetingType, meetingTypes]);

  useEffect(() => {
    if (!isEditMode.current) return;
    if (!meetingId || !customFields.length) return;
    fetchCustomFieldValues();
  }, [meetingId, customFields.length]);

  async function fetchCustomFieldValues() {
    try {
      const { data } = await customFieldsApi.getFieldsValuesByMeetingId(meetingId);

      if (!data?.length) return;

      setValues((prev) => ({
        ...prev,
        customFields: data.map((cfv: any) => ({
          value: cfv.value,
          localizedValue: cfv.localizedValue,
          customFieldId: cfv.customFieldId,
        })),
      }));
    } catch (err) {
      console.error(err);
    }
  }

  async function getMeetingTypes() {
    const response = await meetingTypesApi.getMeetingTypes({
      companyId,
      locationParam: [CustomFieldLocation.MEETING_HOST, 'GUEST_EXTRA_INFO'],
      ...(isCredentialing && { onlyCredentialingType: true }),
    });
    return response.data;
  }

  async function getMeetingLocations() {
    const { data } = await meetingLocationsApi.getMeetingLocations({
      $select: ['name', 'id', 'pictureUrl', 'description'],
      companyId,
    });
    return data;
  }

  function setDefaultMeetingLocation() {
    if (meeting?.meetingLocation) {
      setValues((prev) => ({
        ...prev,
        meetingLocationId: meeting.meetingLocation.id,
        location: null,
      }));
      setLocation(meeting.meetingLocation.name);
      setDisabledLocation(meeting.meetingLocation.name);
    } else if (meeting?.location) {
      setValues((prev) => ({ ...prev, meetingLocationId: undefined, location: meeting.location }));
      setLocation(meeting.location);
    } else if (defaultMeetingLocation) {
      setValues((prev) => ({
        ...prev,
        meetingLocationId: defaultMeetingLocation.id,
        location: null,
      }));
      setLocation(defaultMeetingLocation.name);
      setDisabledLocation(defaultMeetingLocation.name);
    }
  }

  function showDatePicker() {
    setDatePickerVisibility(true);
  }
  function hideDatePicker() {
    setDatePickerVisibility(false);
  }

  function handleConfirm(date: Date) {
    setDatePickerVisibility(!isDatePickerVisible);
    const timezone =
      values.timezone || Localization.getCalendars()[0]?.timeZone || 'America/Sao_Paulo';

    if (currentDatePicker === 'startDatePicker') {
      const start = moment(date).tz(timezone).toDate();
      setValues({ ...values, start, end: moment(date).add(1, 'hours').tz(timezone).toDate() });
    } else {
      const start = values.start || moment().tz(timezone).toDate();
      let end = moment(date).tz(timezone).toDate();
      if (!multiDayMeetingEnabled) {
        end = moment(start)
          .tz(timezone)
          .set({ hour: date.getHours(), minute: date.getMinutes() })
          .toDate();
      }
      if (end < start) {
        return showMessage({ message: translate('editMeeting.invalidDate'), type: 'danger' });
      }
      setValues({ ...values, end });
    }
    hideDatePicker();
  }

  function checkFields(
    fields: CustomFieldWithError[],
    values: CustomFieldValuesModel[] | undefined
  ): CustomFieldWithError[] {
    return fields.map((field) => {
      const data = values?.find(({ customFieldId }) => customFieldId === field.id)?.value;
      return { ...field, hasError: field.required && !field.disabled && !data };
    });
  }

  function handleAddEmailGuest(email: string) {
    if (!validEmail(email)) {
      return showMessage({ message: translate('editMeeting.invalidEmail'), type: 'danger' });
    }
    if ((values.guests ?? []).some((g) => g.email === email)) {
      return showMessage({
        message: translate('editMeeting.emailAlreadyIncluded'),
        type: 'danger',
      });
    }
    setValues((prev) => ({ ...prev, guests: [...(prev.guests ?? []), { email }] }));
  }

  function handleRemoveEmailGuest(email: string) {
    setValues((prev) => ({
      ...prev,
      guests: (prev.guests ?? []).filter((g) => g.email !== email),
    }));
  }

  function handleAddWhatsappGuest(guest: { name: string; phone: string }) {
    if ((values.guests ?? []).some((g) => g.phone === guest.phone)) {
      return showMessage({ message: 'Telefone já adicionado', type: 'danger' });
    }
    setValues((prev) => ({
      ...prev,
      guests: [...(prev.guests ?? []), { phone: guest.phone, name: guest.name || undefined }],
    }));
  }

  function handleRemoveWhatsappGuest(phone: string) {
    setValues((prev) => ({
      ...prev,
      guests: (prev.guests ?? []).filter((g) => g.phone !== phone),
    }));
  }

  const handleAddEmailTag = (tag: string, isHost = false) => {
    if (!validEmail(tag)) {
      return showMessage({ message: translate('editMeeting.invalidEmail'), type: 'danger' });
    }
    if (isHost) {
      const coHosts = values.coHosts ?? [];
      if (coHosts.includes(tag)) {
        return showMessage({
          message: translate('editMeeting.emailAlreadyIncluded'),
          type: 'danger',
        });
      }
      setValues({ ...values, coHosts: [...coHosts, tag] });
    } else {
      handleAddEmailGuest(tag);
    }
  };

  async function handleRemoveEmailTag(tag: string, isHost = false) {
    if (isHost) {
      if (meeting) {
        const [h] = await hostsApi.getHosts({ email: tag });
        if (h) {
          try {
            await hostsApi.deleteMeetingHosts(null, { hostId: h.id, meetingId: meeting.id });
          } catch (error: any) {
            if (error.response.data.name !== 'NotFound') {
              showMessage({ message: translate('meetingSaga.updateMeetingError'), type: 'danger' });
            }
          }
        }
      }
      setValues({ ...values, coHosts: values.coHosts?.filter((e: string) => e !== tag) });
      return;
    }
    handleRemoveEmailGuest(tag);
  }

  async function handleSubmit() {
    if (selectMeetingType?.idNumberSearchEnabled) {
      const hasFoundGuest = (guestsToCheckinByHost ?? []).length > 0;
      const hasNotFoundGuest = !!idNumberGuest;

      if (!hasFoundGuest && !hasNotFoundGuest) {
        showMessage({ message: translate('addDocument'), type: 'danger' });
        return;
      }
    }

    if (selectMeetingType?.restrictVisitFlows) {
      const isValid = filteredFlowTypes.some((f) => f.id === values.visitFlowTypeId);
      if (!isValid) {
        showMessage({
          message: translate(
            isCredentialing
              ? 'editMeeting.visitFlowTypeRequiredCredentialing'
              : 'editMeeting.visitFlowTypeRequired'
          ),
          type: 'danger',
        });
        return;
      }
    }

    if (guestEmailInput) {
      if (!validEmail(guestEmailInput)) {
        showMessage({ message: translate('editMeeting.invalidEmail'), type: 'danger' });
        return;
      }
      handleAddEmailGuest(guestEmailInput);
      setguestEmailInput('');
    }

    if (!meetingTypes?.length) {
      showMessage({ message: translate('editMeeting.emptyList'), type: 'danger' });
      return;
    }

    if (moment().diff(values.start, 'days') > 0 || moment().diff(values.end, 'days') > 0) {
      showMessage({ message: translate('editMeeting.invalidDate'), type: 'danger' });
      return;
    }

    const title = !selectMeetingType?.disableSubject
      ? values.title
      : host?.sector || `${name}-${new Date().toISOString()}`;

    if (!title) {
      showMessage({ message: translate('editMeeting.titleRequired'), type: 'danger' });
      titleRef.current?.focus();
      return;
    }

    if (!values.start || !values.end) {
      showMessage({ message: translate('editMeeting.datesRequired'), type: 'danger' });
      return;
    }

    if (values.end < values.start) {
      showMessage({ message: translate('editMeeting.datesInvalid'), type: 'danger' });
      return;
    }

    if (selectMeetingType?.locationRequired && !selectMeetingType?.disableLocation) {
      const hasLocation = !!values.location?.trim() || !!values.meetingLocationId;
      if (!hasLocation) {
        showMessage({ message: translate('editMeeting.fieldsRequired'), type: 'danger' });
        locationRef.current?.focus();
        return;
      }
    }

    const maxScheduleHours = (
      selectMeetingType as MeetingTypesModel & { maxScheduleHours?: number | null }
    )?.maxScheduleHours;

    if (
      maxScheduleHours &&
      maxScheduleHours > 0 &&
      moment(values.end).isAfter(moment(values.start).add(maxScheduleHours, 'hours'))
    ) {
      showMessage({
        message: translate('maxScheduleHoursExceeded', { value: maxScheduleHours }),
        type: 'danger',
      });
      return;
    }

    const checkedFields = checkFields(
      customFields,
      values.customFields as unknown as CustomFieldValuesModel[]
    );

    if (checkedFields.some(({ hasError }) => hasError)) {
      showMessage({ message: translate('editMeeting.fieldsRequired'), type: 'danger' });
      return;
    }

    if (
      selectMeetingType?.idNumberSearchEnabled &&
      idNumberGuestFields.length > 0 &&
      idNumberGuest
    ) {
      const extraFields = idNumberGuest.extraFields ?? {};

      const missingRequired = idNumberGuestFields.find((field) => {
        if (!field.required) return false;
        if (field.type === 'EMAIL') return !idNumberGuest.email?.trim();
        if (field.type === 'PHONE') return !idNumberGuest.phone?.trim();
        const value = extraFields[field.id!];
        return !value || !value.trim();
      });

      if (missingRequired) {
        const fieldName =
          missingRequired.placeholder?.[i18n.language] ||
          missingRequired.placeholder?.['pt-BR'] ||
          missingRequired.name ||
          '';
        showMessage({ message: translate('invalidField', { type: fieldName }), type: 'danger' });
        return;
      }

      const phoneField = idNumberGuestFields.find((f) => f.type === 'PHONE');
      if (phoneField && idNumberGuest.phone) {
        if (!isValidPhoneNumber(idNumberGuest.phone)) {
          const fieldName =
            phoneField.placeholder?.[i18n.language] ||
            phoneField.placeholder?.['pt-BR'] ||
            phoneField.name ||
            '';
          showMessage({ message: translate('invalidField', { type: fieldName }), type: 'danger' });
          return;
        }
      }

      const emailField = idNumberGuestFields.find((f) => f.type === 'EMAIL');
      if (emailField && idNumberGuest.email) {
        if (!validEmail(idNumberGuest.email)) {
          const fieldName =
            emailField.placeholder?.[i18n.language] ||
            emailField.placeholder?.['pt-BR'] ||
            emailField.name ||
            '';
          showMessage({ message: translate('invalidField', { type: fieldName }), type: 'danger' });
          return;
        }
      }
    }

    const baseObj = {
      ...values,
      companyId,
      meetingTypeId: selectMeetingType?.id || null,
      lang: i18n.language,
      guests: idNumberGuest
        ? [
            {
              idNumber: idNumberGuest.idNumber,
              email: idNumberGuest.email,
              phone: idNumberGuest.phone,
            },
          ]
        : values.guests ?? [],
      title,
      ...(selectMeetingType?.idNumberSearchEnabled && {
        idNumberGuest,
        guestsToCheckinByHost,
      }),
    };

    if (meeting) {
      await updateMeeting({ ...baseObj, id: meeting.id });

      queryClient.invalidateQueries({ queryKey: GUEST_LINKS_QUERY_KEY(meeting.id) });
      queryClient.removeQueries({ queryKey: GUEST_LINKS_QUERY_KEY(meeting.id) });
    } else {
      createMeeting({ ...baseObj, limit: limitOfLinkInvites });
    }
  }

  function handleBackNavigation() {
    isCredentialing
      ? navigation.navigate(AppRoute.CREDENTIALING)
      : navigation.navigate(AppRoute.MEETINGS);
  }

  async function searchApiLocation(search: string) {
    setValues({ ...values, location: search, meetingLocationId: undefined });
    setLocation(search);
    const data = search
      ? allLocations.filter(({ name }) => name.toLowerCase().includes(search.toLowerCase()))
      : allLocations;
    setLocations(data);
  }

  const LocationList = useCallback(() => {
    if (!hasInputFocus) return null;
    return (
      <FlatList
        nestedScrollEnabled
        data={locations.length ? locations : allLocations}
        keyExtractor={(item) => item.id}
        style={styles.list}
        renderItem={({ item }) => (
          <TouchableOpacity
            onPress={() => {
              setValues({ ...values, meetingLocationId: item.id, location: null });
              setHasInputFocus(false);
              setDisabledLocation(item.name);
              setLocation(item.name);
              setLocations(allLocations);
              notesRef.current?.focus();
            }}
          >
            <View style={styles.autocompleteButton}>
              {item.pictureUrl ? (
                <Image style={styles.avatar} source={{ uri: item.pictureUrl }} />
              ) : (
                <View style={styles.avatar}>
                  <Feather name="map-pin" style={styles.icon} />
                </View>
              )}
              <Text style={styles.autocompleteText}>{item.name}</Text>
              <Text numberOfLines={8} style={styles.autocompleteSmall}>
                {item.description}
              </Text>
            </View>
          </TouchableOpacity>
        )}
      />
    );
  }, [locations, hasInputFocus]);

  if (meetingTypesLoading || allLocationsLoading) {
    return (
      <View style={styles.container}>
        <Spin visible />
      </View>
    );
  }

  const { coHosts, end, guests, message, notes, start, title } = values;

  const displayEmailGuests = (guests ?? []).filter((g) => g.email).map((g) => g.email!);
  const displayWhatsappGuests = (guests ?? [])
    .filter((g) => g.phone && !g.email)
    .map((g) => ({ phone: g.phone!, name: g.name ?? '' }));

  if (meetingTypesLoading || allLocationsLoading) {
    return (
      <View style={styles.container}>
        <Spin visible />
      </View>
    );
  }

  if (!meetingTypes?.length) {
    return (
      <ContainerWidthLimit>
        <View style={styles.container}>
          <Header
            backgroundColor={themes.colors.white}
            handleBack={handleBackNavigation}
            title={
              isCredentialing
                ? translate('newCredentialing')
                : translate('editMeeting.newMeetingTitle')
            }
          />
          <View style={styles.emptyStateContainer}>
            <Feather name="alert-circle" style={styles.emptyStateIcon} />
            <Text style={styles.emptyStateTitle}>
              {translate(
                isCredentialing ? 'editMeeting.noCredentialingTypes' : 'editMeeting.noMeetingTypes'
              )}
            </Text>
            <Text style={styles.emptyStateSubtitle}>
              {translate('editMeeting.contactAdminToProceed')}
            </Text>
          </View>
        </View>
      </ContainerWidthLimit>
    );
  }

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Spin visible={loading} />
        <Header
          backgroundColor={themes.colors.white}
          handleBack={handleBackNavigation}
          title={
            meeting
              ? isCredentialing
                ? translate('editCredentialing')
                : translate('editMeeting.editMeetingTitle')
              : isCredentialing
              ? translate('newCredentialing')
              : translate('editMeeting.newMeetingTitle')
          }
        />
        <KeyboardAvoidingView
          style={styles.container}
          keyboardVerticalOffset={40}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.content}>
            <ScrollView
              keyboardDismissMode="interactive"
              keyboardShouldPersistTaps="always"
              showsVerticalScrollIndicator={false}
            >
              <TouchableWithoutFeedback
                onPress={() => {
                  if (Platform.OS !== 'web') Keyboard.dismiss();
                  setHasInputFocus(false);
                }}
              >
                <View>
                  {Platform.OS === 'web' ? (
                    <WebDateTimePicker
                      visible={isDatePickerVisible}
                      handleClose={hideDatePicker}
                      selectedDate={currentDatePicker === 'startDatePicker' ? start : end}
                      handleConfirm={handleConfirm}
                      locale={locale.code}
                      mode={
                        !multiDayMeetingEnabled && currentDatePicker === 'endDatePicker'
                          ? 'time'
                          : 'datetime'
                      }
                    />
                  ) : (
                    <DateTimePickerModal
                      date={
                        start
                          ? new Date(currentDatePicker === 'endDatePicker' && end ? end : start)
                          : new Date()
                      }
                      textColor={themes.colors.primary}
                      isVisible={isDatePickerVisible}
                      mode={
                        !multiDayMeetingEnabled && currentDatePicker === 'endDatePicker'
                          ? 'time'
                          : 'datetime'
                      }
                      onConfirm={handleConfirm}
                      onCancel={hideDatePicker}
                      locale={locale.code}
                      confirmTextIOS={translate('confirmButton')}
                      cancelTextIOS={translate('cancelButton')}
                    />
                  )}

                  <View style={styles.inputsContainer}>
                    {meetingTypes?.length > 1 && (
                      <>
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.formLabel, { textAlign: 'center' }]}>
                            {translate(
                              isCredentialing
                                ? 'selectAccreditationType'
                                : 'editMeeting.selectMeetingType'
                            )}
                          </Text>
                        </View>
                        <View style={styles.formInput}>
                          <ModalSelector
                            value={selectMeetingType?.id || ''}
                            disabled={!!meeting || !selectMeetingType}
                            options={meetingTypes}
                            onChange={(option: any) => setSelectMeetingType(option.data)}
                          />
                        </View>
                        {!meetingTypesLoading && !meetingTypes?.length && (
                          <Text
                            style={{
                              textAlign: 'center',
                              color: themes.colors.danger,
                              fontSize: 14,
                            }}
                          >
                            {translate('editMeeting.emptyList')}
                          </Text>
                        )}
                      </>
                    )}
                  </View>

                  {filteredFlowTypes.length > 1 && (
                    <View style={styles.inputsContainer}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.formLabel, { textAlign: 'center' }]}>
                          {translate('editMeeting.selectVisitFlowType')}
                        </Text>
                      </View>
                      <View style={styles.formInput}>
                        <ModalSelector
                          value={values.visitFlowTypeId || ''}
                          options={filteredFlowTypes}
                          onChange={(option: any) =>
                            setValues({ ...values, visitFlowTypeId: option.key })
                          }
                        />
                      </View>
                    </View>
                  )}

                  {selectMeetingType?.idNumberSearchEnabled && (
                    <View style={styles.inputsContainer}>
                      <CardholderSearchApp
                        companyId={companyId}
                        primaryKey={primaryKey}
                        lockedGuest={idNumberGuest}
                        idNumberGuestFields={idNumberGuestFields}
                        guestsToCheckinByHost={guestsToCheckinByHost}
                        onFoundAddToCheckin={(guest) =>
                          setGuestsToCheckinByHost((prev) => [...prev, guest])
                        }
                        onFoundRemoveFromCheckin={(guest) =>
                          setGuestsToCheckinByHost((prev) => prev.filter((g) => g !== guest))
                        }
                        onNotFoundSetGuest={setIdNumberGuest}
                        isCredentialing={isCredentialing}
                      />
                    </View>
                  )}

                  {!selectMeetingType?.disableSubject && (
                    <Input
                      label={translate(
                        isCredentialing ? 'accreditationTitle' : 'editMeeting.subject'
                      )}
                      returnKeyType="next"
                      autoCorrect
                      autoCapitalize="sentences"
                      ref={titleRef}
                      value={title}
                      style={styles.formInput}
                      placeholder={translate(
                        isCredentialing
                          ? 'accreditationTitlePlaceholder'
                          : 'editMeeting.subjectPlaceholder'
                      )}
                      onChangeText={(title) => setValues({ ...values, title })}
                      onSubmitEditing={Keyboard.dismiss}
                      required
                      icon="type"
                      containerStyle={{ marginBottom: 12 }}
                    />
                  )}

                  {/* Dates */}
                  {(!selectMeetingType?.disableDate || !selectMeetingType?.disableTime) && (
                    <>
                      <TouchableOpacity
                        style={styles.dateContainer}
                        onPress={() => {
                          setCurrentDatePicker('startDatePicker');
                          showDatePicker();
                        }}
                      >
                        <Input
                          label={translate('editMeeting.start')}
                          required
                          autoCorrect
                          icon="calendar"
                          pointerEvents="none"
                          style={styles.formInput}
                          editable={false}
                          value={
                            start ? format(new Date(start), 'dd MMMM yyyy H:mm', { locale }) : ''
                          }
                          placeholder={translate('editMeeting.startPlaceholder')}
                          containerStyle={{ marginBottom: 12 }}
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.dateContainer}
                        onPress={() => {
                          setCurrentDatePicker('endDatePicker');
                          showDatePicker();
                        }}
                      >
                        <Input
                          label={translate('editMeeting.end')}
                          pointerEvents="none"
                          required
                          autoCorrect
                          style={styles.formInput}
                          editable={false}
                          value={end ? format(new Date(end), 'dd MMMM yyyy H:mm', { locale }) : ''}
                          placeholder={translate('editMeeting.endPlaceholder')}
                          icon="calendar"
                          containerStyle={{ marginBottom: 12 }}
                        />
                      </TouchableOpacity>
                    </>
                  )}

                  {!selectMeetingType?.disableLocation && (
                    <View style={styles.inputsContainer}>
                      {!disabledLocation && (
                        <View style={styles.listView}>
                          <LocationList />
                        </View>
                      )}
                      {!!disabledLocation && (
                        <View>
                          <Text style={styles.formLabel}>{translate('editMeeting.location')}</Text>
                          <View style={styles.formInput}>
                            <View style={styles.formInputTag}>
                              <Text numberOfLines={1}>{disabledLocation}</Text>
                              <Feather
                                name="x"
                                style={styles.closeIcon}
                                onPress={() => {
                                  setValues({
                                    ...values,
                                    meetingLocationId: undefined,
                                    location: null,
                                  });
                                  setDisabledLocation('');
                                  setLocation('');
                                }}
                              />
                            </View>
                          </View>
                        </View>
                      )}
                      {!disabledLocation && (
                        <Input
                          label={translate('editMeeting.location')}
                          placeholder={translate('editMeeting.locationPlaceholder')}
                          ref={locationRef}
                          autoCorrect
                          onFocus={() => setHasInputFocus(true)}
                          autoCapitalize="sentences"
                          value={location}
                          style={styles.formInput}
                          onChangeText={searchApiLocation}
                          onSubmitEditing={() => notesRef.current?.focus()}
                          icon="map-pin"
                          required={!!selectMeetingType?.locationRequired}
                        />
                      )}
                    </View>
                  )}

                  {!selectMeetingType?.disableMessage && (
                    <Input
                      label={translate('editMeeting.message')}
                      autoCapitalize="sentences"
                      autoCorrect
                      ref={messageRef}
                      value={message || ''}
                      style={styles.formInput}
                      placeholder={translate('editMeeting.messagePlaceholder')}
                      onChangeText={(message) => setValues({ ...values, message })}
                      onSubmitEditing={() => locationRef.current?.focus()}
                      icon="align-left"
                      containerStyle={{ marginBottom: 12 }}
                    />
                  )}

                  {!selectMeetingType?.disableNotes && (
                    <Input
                      label={translate('editMeeting.notes')}
                      autoCapitalize="sentences"
                      autoCorrect
                      ref={notesRef}
                      value={notes || ''}
                      style={styles.formInput}
                      placeholder={translate('editMeeting.notesPlaceholder')}
                      onChangeText={(notes) => setValues({ ...values, notes })}
                      onSubmitEditing={Keyboard.dismiss}
                      multiline
                      icon="info"
                    />
                  )}

                  <CustomFieldsInput
                    setCustomFieldsValues={(customFields) => setValues({ ...values, customFields })}
                    customFields={customFields}
                    customFieldsValues={values.customFields}
                  />

                  {!meeting &&
                    !selectMeetingType?.disableGuests &&
                    !selectMeetingType?.disableLinks &&
                    !selectMeetingType?.idNumberSearchEnabled && (
                      <View style={[styles.inputsContainer, styles.selectInputsContainer]}>
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.formLabel, styles.selectFormLabel]}>
                            {translate('editMeeting.invitees')}
                          </Text>
                        </View>
                        <View style={styles.selectContainer}>
                          <View style={[styles.formInput, styles.selectFormInput]}>
                            <ModalNumberSelector
                              type="numbers"
                              value={limitOfLinkInvites}
                              onChange={(option) => setLimitOfLinkInvites(option.key)}
                            />
                          </View>
                        </View>
                      </View>
                    )}

                  {!selectMeetingType?.disableCoHosts && (
                    <View style={styles.inputsContainer}>
                      <Text style={styles.formLabel}>{translate('editMeeting.hostList')}</Text>
                      <EmailTagInput
                        emailsArray={coHosts ?? []}
                        removeEmailTag={(email) => handleRemoveEmailTag(email, true)}
                        onSubmitEditing={(email) => handleAddEmailTag(email, true)}
                        email={hostEmailInput}
                        onChangeEmail={sethostEmailInput}
                        isHostList
                      />
                    </View>
                  )}

                  {!selectMeetingType?.disableGuests &&
                    !selectMeetingType?.idNumberSearchEnabled && (
                      <View style={styles.inputsContainer}>
                        <Text style={styles.formLabel}>
                          {translate('editMeeting.inviteesList')}
                        </Text>

                        {isWhatsappEnabled && (
                          <View style={styles.tabContainer}>
                            <TouchableOpacity
                              style={[
                                styles.tabButton,
                                activeTab === 'email' && styles.activeTabButton,
                              ]}
                              onPress={() => {
                                setActiveTab('email');
                              }}
                            >
                              <Feather
                                name="mail"
                                size={14}
                                color={
                                  activeTab === 'email'
                                    ? themes.colors.primary
                                    : themes.colors.darkQuaternary
                                }
                              />
                              <Text
                                style={
                                  activeTab === 'email' ? styles.activeTabText : styles.tabText
                                }
                              >
                                Email
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[
                                styles.tabButton,
                                activeTab === 'whatsapp' && styles.activeTabButton,
                              ]}
                              onPress={() => {
                                setActiveTab('whatsapp');
                              }}
                            >
                              <Feather
                                name="message-circle"
                                size={14}
                                color={
                                  activeTab === 'whatsapp'
                                    ? '#25D366'
                                    : themes.colors.darkQuaternary
                                }
                              />
                              <Text
                                style={
                                  activeTab === 'whatsapp'
                                    ? styles.activeTabTextWhatsapp
                                    : styles.tabText
                                }
                              >
                                WhatsApp
                              </Text>
                            </TouchableOpacity>
                          </View>
                        )}

                        {!isWhatsappEnabled || activeTab === 'email' ? (
                          <EmailTagInput
                            emailsArray={displayEmailGuests}
                            removeEmailTag={handleRemoveEmailGuest}
                            onSubmitEditing={handleAddEmailGuest}
                            email={guestEmailInput}
                            onChangeEmail={setguestEmailInput}
                            loadingMore={loadingInvitees}
                          />
                        ) : (
                          <WhatsappTagInput
                            whatsappArray={displayWhatsappGuests}
                            onAdd={handleAddWhatsappGuest}
                            onRemove={handleRemoveWhatsappGuest}
                            loadingMore={loadingInvitees}
                          />
                        )}
                      </View>
                    )}

                  <TouchableOpacity style={styles.button} onPress={handleSubmit}>
                    <Text style={styles.buttonText}>{translate('general.save')}</Text>
                  </TouchableOpacity>
                </View>
              </TouchableWithoutFeedback>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: themes.colors.white },
  content: { flex: 1, padding: 24, paddingTop: 0, paddingBottom: 0 },
  inputsContainer: { marginBottom: 24 },
  selectInputsContainer: { flexDirection: 'row', alignItems: 'center' },
  selectContainer: { alignItems: 'flex-end', borderBottomWidth: 0, flex: 1 },
  formLabel: { color: themes.colors.darkQuaternary, fontFamily: fontFamily.medium, fontSize: 17 },
  selectFormLabel: { width: 200 },
  formInput: {
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.regular,
    flex: 1,
    fontSize: 15,
    paddingVertical: 8,
  },
  formInputTag: {
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.regular,
    fontSize: 15,
    borderRadius: 4,
    borderWidth: 2,
    borderStyle: 'solid',
    padding: 6,
    borderColor: themes.colors.lightSecondary,
    backgroundColor: transparentize(0.7, themes.colors.lightQuaternary),
    paddingHorizontal: 8,
    marginHorizontal: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectFormInput: { width: 100 },
  dateContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  button: {
    backgroundColor: themes.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    marginBottom: 8,
    height: 44,
  },
  buttonText: { color: themes.colors.white },
  icon: { color: themes.colors.lightSecondary, fontSize: 18 },
  closeIcon: { fontSize: 18, color: themes.colors.darkSecondary },
  autocompleteButton: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderColor: themes.colors.lightSecondary,
    borderBottomWidth: 1,
  },
  autocompleteText: {
    color: themes.colors.darkSecondary,
    fontSize: 14,
    marginHorizontal: 12,
    maxWidth: '40%',
  },
  avatar: {
    zIndex: 1000,
    width: 42,
    height: 42,
    borderRadius: 100,
    backgroundColor: themes.colors.graySecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  autocompleteSmall: { flex: 1, fontSize: 12, color: themes.colors.graySecondary },
  list: {
    borderColor: themes.colors.lightSecondary,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    maxHeight: 260,
  },
  listView: {
    position: 'absolute',
    bottom: 80,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: lighten(0.2, themes.colors.lightPrimary),
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: themes.colors.lightPrimary,
    borderRadius: 10,
    padding: 4,
    marginTop: 10,
    marginBottom: 14,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 8,
  },
  activeTabButton: {
    backgroundColor: themes.colors.white,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabButtonLocked: { opacity: 0.4 },
  tabText: { color: themes.colors.darkQuaternary, fontSize: 14, fontFamily: fontFamily.regular },
  activeTabText: { color: themes.colors.primary, fontSize: 14, fontFamily: fontFamily.medium },
  activeTabTextWhatsapp: { color: '#25D366', fontSize: 14, fontFamily: fontFamily.medium },
  tabTextLocked: {
    color: themes.colors.graySecondary,
    fontSize: 14,
    fontFamily: fontFamily.regular,
  },
  emptyStateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 12,
  },
  emptyStateIcon: {
    fontSize: 48,
    color: themes.colors.graySecondary,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontFamily: fontFamily.medium,
    color: themes.colors.darkSecondary,
    textAlign: 'center',
  },
  emptyStateSubtitle: {
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: themes.colors.darkQuaternary,
    textAlign: 'center',
    lineHeight: 20,
  },
});
